import {
  Box,
  Divider,
  Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import * as _ from "lodash";
import moment from "moment-timezone";
import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitVendorAvailibility } from "../../../api/projects";
import {
  DefaultButton,
  PrimaryButton,
  RequestCoordinationButton,
} from "../../../components/form";
import { AppAlert } from "../../../components/form/alert";
import { FormHelperTextError } from "../../../components/form/errors";
import { SlotSelectionModal } from "../../../components/invitations";
import FlexBox from "../../../components/layouts/Flexbox";
import { AddMembersModal } from "../../../components/modal/add-members-modal";
import AlertDialog from "../../../components/modal/alert-dialog";
import { GLOBAL_TEXT } from "../../../constants";
import { ROUTE_PATHS } from "../../../routes/paths";
import {
  DATE_FORMATS,
  getFromAndToDateTimeRange,
} from "../../../services/utils/date-and-time.utils";
import { useAppDispatch, useAppSelector } from "../../../store";
import { showNotification } from "../../../store/modules/UI";
import ProfileViewModal from "../../../components/modal/profile-view-modal";
import AmbassadorProfile from "../../my-profile/ambassador-profile";
import {
  checkTimeslotSelectionForLateNight,
  createSelectedClientSlotsPayload,
  validateSlots,
} from "../utils";
import { DefaultCalendlyEventTypeSelector } from "../../../components/calendly";
import { AmbassadorInfoSection } from "./AmbassadorInfoSection";
import { ManualSlotsSection } from "./ManualSlotsSection";
import { SlotSelectionModeToggle } from "./SlotSelectionModeToggle";
import { SlotModalState, VendorSlot } from "./types";

function VendorSlotSelectionModal({
  onCancel,
  data,
  onAvailibilitySend,
  onSendCalendlyInvite,
}: any) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userData = useAppSelector((state) => state.auth.user);

  const [showSlotSelectionConfirmAlert, setShowSlotSelectionConfirmAlert] =
    useState(false);
  const [preserveSelection, setPreserveSelection] = useState<{
    slot?: VendorSlot;
    slots?: VendorSlot[];
    modalState: SlotModalState;
  } | null>(null);
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [submitClicked, setSubmitClicked] = useState(false);
  const [openAmbassadorProfileModal, setOpenAmbassadorProfileModal] =
    useState(false);
  const [invitationAmbassadorList, setInvitationAmbassadorList] = useState<any>(
    {
      ...data,
      slots: data?.slots?.length ? data.slots : [],
    }
  );
  const [submitting, setSubmitting] = useState(false);
  const [lateNightOnceConfirmed, setLateNightOnceConfirmed] = useState(false);
  const [slotSelectionMode, setSlotSelectionMode] = useState<
    "calendly" | "manual"
  >(userData?.isCalendly ? "calendly" : "manual");
  const [slotModalState, setSlotModalState] = useState<SlotModalState>(null);
  const [isUpdatingAvailability, setIsUpdatingAvailability] = useState(false);

  const [emailIds, setEmailds] = useState<{
    list: string[];
    invalidEmailIds: string[];
  }>({
    list: [],
    invalidEmailIds: [],
  });
  const [soloAttendanceChecked, setSoloAttendanceChecked] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState<
    "manual" | "calendly" | null
  >(null);
  const [attendeesConfirmed, setAttendeesConfirmed] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const slots: VendorSlot[] = invitationAmbassadorList?.slots || [];
  const invitationId = data?.id;
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const ambassadorTimezoneValue =
    invitationAmbassadorList?.ambassador?.timezone?.value;
  const ambassadorTimezoneAbbr = ambassadorTimezoneValue
    ? moment().tz(ambassadorTimezoneValue).format("z")
    : undefined;
  const manualSelectionEnabled =
    !userData?.isCalendly || slotSelectionMode === "manual";
  const shouldShowHelpButton =
    data?.availableTimeSlots?.length > 0 &&
    data?.suggestedTimeSlots?.length > 0;
  const stepLabels = ["Details", "Availability"];
  const steps = manualSelectionEnabled ? stepLabels : ["Details"];
  const activeStep = Math.min(currentStep, steps.length - 1);

  const handleAddMembersSubmit = (data: {
    emails: string[];
    soloAttendance: boolean;
  }) => {
    setEmailds({
      list: data.emails,
      invalidEmailIds: [],
    });
    setSoloAttendanceChecked(data.soloAttendance);
    setShowAddMembersModal(false);
    setFormErrorMessage("");
    setPendingSubmission(null);
    setAttendeesConfirmed(true);


    if (manualSelectionEnabled && currentStep === 0) {
      setCurrentStep(1);
    }


    if (data.soloAttendance && invitationAmbassadorList?.slots?.length >= 3) {
      setOpenAmbassadorProfileModal(true);
    }
  };

  const proceedWithCalendlySubmission = () => {
    setFormErrorMessage("");
    setOpenAmbassadorProfileModal(true);
  };

  const proceedWithManualSubmission = () => {
    const validation = validateSlots(invitationAmbassadorList);
    setInvitationAmbassadorList(validation.updatedList);

    if (validation.isFormValid && emailIds.invalidEmailIds.length === 0) {
      setOpenAmbassadorProfileModal(true);
      setFormErrorMessage("");
    } else {
      setFormErrorMessage(
        emailIds.invalidEmailIds.length > 0
          ? "Invalid email addresses"
          : validation.formMessage
      );
    }
  };

  const buildSlot = (
    from: Date,
    to: Date,
    isTimeRange: boolean
  ): VendorSlot => {
    const formatInAmbassadorTimezone = (value: Date): string | undefined => {
      if (!value) return undefined;
      return ambassadorTimezoneValue
        ? moment(value)
          .tz(ambassadorTimezoneValue)
          .format(DATE_FORMATS.fullDateWithTime)
        : moment(value).format(DATE_FORMATS.fullDateWithTime);
    };

    return {
      selectedSlotTimeFrom: from,
      selectedSlotTimeTo: to,
      isTimeRange,
      error: "",
      convertedSlotTimeFrom: formatInAmbassadorTimezone(from),
      convertedSlotTimeTo: formatInAmbassadorTimezone(to),
    };
  };

  const commitSlot = (slot: VendorSlot, modalState: SlotModalState) => {
    const listClone = _.cloneDeep(invitationAmbassadorList);

    if (modalState?.mode === "edit" && modalState.index !== null) {
      listClone.slots[modalState.index] = slot;
    } else {
      listClone.slots = [...(listClone.slots || []), slot];
    }

    if (submitClicked) {
      const validation = validateSlots(listClone);
      setInvitationAmbassadorList(validation.updatedList);
      setFormErrorMessage(validation.isFormValid ? "" : validation.formMessage);
    } else {
      setInvitationAmbassadorList(listClone);
      setFormErrorMessage("");
    }
  };

  const commitSlots = (slots: VendorSlot[]) => {
    if (slots.length === 0) return;
    const listClone = _.cloneDeep(invitationAmbassadorList);
    listClone.slots = [...(listClone.slots || []), ...slots];
    if (submitClicked) {
      const validation = validateSlots(listClone);
      setInvitationAmbassadorList(validation.updatedList);
      setFormErrorMessage(validation.isFormValid ? "" : validation.formMessage);
    } else {
      setInvitationAmbassadorList(listClone);
      setFormErrorMessage("");
    }
  };

  const attemptSlotPersist = (
    slot: VendorSlot,
    modalState: SlotModalState,
    confirmed = false
  ) => {
    if (!modalState) {
      return;
    }

    const requiresConfirmation =
      !confirmed &&
      !lateNightOnceConfirmed &&
      slot.convertedSlotTimeFrom &&
      checkTimeslotSelectionForLateNight(slot.convertedSlotTimeFrom);

    if (requiresConfirmation) {
      setPreserveSelection({ slot, modalState });
      setShowSlotSelectionConfirmAlert(true);
      setSlotModalState(null);
      return;
    }

    commitSlot(slot, modalState);
    setSlotModalState(null);
  };

  const handleSlotSubmit = (payload: any) => {
    if (!slotModalState) return;

    const payloads = Array.isArray(payload) ? payload : [payload];
    const slots: VendorSlot[] = [];

    for (const p of payloads) {
      const { dateFrom, dateTo } = getFromAndToDateTimeRange(p);
      const slot = buildSlot(dateFrom, dateTo, p?.type === "datetimerange");
      slots.push(slot);
    }

    const anyRequiresConfirmation = slots.some(
      (s) =>
        !lateNightOnceConfirmed &&
        s.convertedSlotTimeFrom &&
        checkTimeslotSelectionForLateNight(s.convertedSlotTimeFrom)
    );

    if (anyRequiresConfirmation) {
      if (slots.length === 1) {
        setPreserveSelection({
          slot: slots[0],
          modalState: slotModalState,
        });
      } else {
        setPreserveSelection({
          slots,
          modalState: slotModalState,
        });
      }
      setShowSlotSelectionConfirmAlert(true);
      setSlotModalState(null);
      return;
    }

    if (isUpdatingAvailability) {
      const listClone = _.cloneDeep(invitationAmbassadorList);
      listClone.slots = slots;
      if (submitClicked) {
        const validation = validateSlots(listClone);
        setInvitationAmbassadorList(validation.updatedList);
        setFormErrorMessage(validation.isFormValid ? "" : validation.formMessage);
      } else {
        setInvitationAmbassadorList(listClone);
        setFormErrorMessage("");
      }
      setSlotModalState(null);
      setIsUpdatingAvailability(false);
      return;
    }

    if (slots.length === 1) {
      attemptSlotPersist(slots[0], slotModalState);
    } else {
      commitSlots(slots);
      setSlotModalState(null);
    }
  };

  const handleOnSlotAdd = () => {
    setIsUpdatingAvailability(slots.length > 0);
    setSlotModalState({ mode: "create", index: null });
  };

  const handleOnSlotEdit = (index: number) => {
    setSlotModalState({ mode: "edit", index });
  };

  const handleOnSlotRemove = (index: number) => {
    const clonedList = _.cloneDeep(invitationAmbassadorList);
    clonedList.slots.splice(index, 1);

    const validation = validateSlots(clonedList);
    setInvitationAmbassadorList(validation.updatedList);

    if (submitClicked) {
      setFormErrorMessage(validation.isFormValid ? "" : validation.formMessage);
    } else {
      setFormErrorMessage("");
    }
  };

  const onRequestCoordinationSuccess = () => {
    navigate(ROUTE_PATHS.PROJECT.LIST);
  };

  const handleSlotSelectionModeChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const nextMode = event.target.value as "calendly" | "manual";
    setSlotSelectionMode(nextMode);
    setFormErrorMessage("");
    setSubmitClicked(false);

    if (nextMode === "manual") {
      if (emailIds.invalidEmailIds.length > 0) {
        setFormErrorMessage("Invalid email addresses");
        setCurrentStep(0);
      } else {
        setCurrentStep(1);
      }
    } else {
      setCurrentStep(0);
    }
  };

  const validateAttendeeRequirement = (): boolean => {

    const hasValidEmails =
      emailIds.list.length > 0 && emailIds.invalidEmailIds.length === 0;
    const hasSoloAttendance = soloAttendanceChecked;

    return hasValidEmails || hasSoloAttendance;
  };

  const handleNextStep = () => {
    if (emailIds.invalidEmailIds.length > 0) {
      setFormErrorMessage("Invalid email addresses");
      return;
    }


    if (!manualSelectionEnabled) {
      onSubmit();
      return;
    }

    setFormErrorMessage("");
    setCurrentStep(1);
  };

  const handleBackStep = () => {
    setCurrentStep(0);
    setFormErrorMessage("");
  };

  const onSubmit = () => {
    setSubmitClicked(true);

    if (emailIds.invalidEmailIds.length > 0) {
      setFormErrorMessage("Invalid email addresses");
      return;
    }


    if (!validateAttendeeRequirement()) {


      if (currentStep === 0 && manualSelectionEnabled) {
        setFormErrorMessage(
          "Please add attendee email addresses or confirm that no additional representatives will join"
        );
      }
      if (userData?.isCalendly && slotSelectionMode === "calendly") {
        setPendingSubmission("calendly");
      } else {
        setPendingSubmission("manual");
      }
      setShowAddMembersModal(true);
      return;
    }


    setFormErrorMessage("");
    if (userData?.isCalendly && slotSelectionMode === "calendly") {
      setOpenAmbassadorProfileModal(true);
      return;
    }

    proceedWithManualSubmission();
  };

  const onConfirm = async () => {
    if (userData?.isCalendly && slotSelectionMode === "calendly") {
      onSendCalendlyInvite();
      return;
    }

    setSubmitting(true);
    const validation = validateSlots(invitationAmbassadorList);
    setInvitationAmbassadorList(validation.updatedList);

    if (!validation.isFormValid) {
      setFormErrorMessage(validation.formMessage);
      setSubmitting(false);
      return;
    }

    const payload = createSelectedClientSlotsPayload(invitationAmbassadorList);


    const requestPayload = {
      ...payload,
      availableTimeSlots: payload.availableTimeSlots.map((slot: any) => ({
        from: new Date(slot.from).toUTCString(),
        to: new Date(slot.to).toUTCString(),
      })),
      ...(emailIds.list.length > 0 && {
        additionalMembers: emailIds.list.map((email) => ({ email })),
      }),
    };

    const response: any = await dispatch(
      submitVendorAvailibility(requestPayload, data?.id)
    );

    if (response.success) {
      onAvailibilitySend();
    } else {
      setOpenAmbassadorProfileModal(false);

      if (response.isCustom) {
        setFormErrorMessage(response.message);
        setSubmitting(false);
        return;
      }

      dispatch(
        showNotification({
          message:
            response.message ||
            "Invalid slot selection. Please check all the slot selections!",
          type: "error",
        })
      );
      setFormErrorMessage(response.message);
    }

    setSubmitting(false);
  };

  const handleLateNightConfirm = () => {
    if (!preserveSelection) return;
    if (preserveSelection.slots?.length) {
      commitSlots(preserveSelection.slots);
    } else if (preserveSelection.slot) {
      commitSlot(preserveSelection.slot, preserveSelection.modalState);
    }
    setLateNightOnceConfirmed(true);
    setPreserveSelection(null);
    setShowSlotSelectionConfirmAlert(false);
  };

  const handleLateNightDismiss = () => {
    setPreserveSelection(null);
    setShowSlotSelectionConfirmAlert(false);
  };

  const handleSlotModalClose = () => {
    setSlotModalState(null);
    setIsUpdatingAvailability(false);
  };

  useEffect(() => {
    if (!manualSelectionEnabled && currentStep > 0) {
      setCurrentStep(0);
    }
  }, [manualSelectionEnabled, currentStep]);

  useEffect(() => {
    if (data) {
      setInvitationAmbassadorList({
        ...data,
        slots: data?.slots?.length ? data.slots : [],
      });
    }
  }, [data]);

  return (
    <>
      <Stack sx={{ flex: 1, display: "flex", flexDirection: "column", height: "100%" }}>
        <Box
          flex={1}
          overflow="auto"
        >
          {manualSelectionEnabled && (
            <Stepper
              activeStep={activeStep}
              sx={{ px: { xs: 0, sm: 1 }, mt: 3 }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          <Box mt={manualSelectionEnabled ? 3 : 0}>
            {currentStep === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <AmbassadorInfoSection
                    ambassador={invitationAmbassadorList?.ambassador}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                {userData?.isCalendly && (
                  <Grid item xs={12}>
                    <SlotSelectionModeToggle
                      value={slotSelectionMode}
                      onChange={handleSlotSelectionModeChange}
                    />
                  </Grid>
                )}

                {userData?.isCalendly && slotSelectionMode === "calendly" && (
                  <Grid item xs={12}>
                    <DefaultCalendlyEventTypeSelector
                      enabled
                      formLabelId="vendor-slot-calendly-event-type-label"
                      description="Your Calendly invite will use this event type. You can change it before sending."
                    />
                  </Grid>
                )}

                {!manualSelectionEnabled &&
                  attendeesConfirmed &&
                  validateAttendeeRequirement() && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            Attendees
                          </Typography>
                          <PrimaryButton
                            onClick={() => setShowAddMembersModal(true)}
                          >
                            Change Attendees
                          </PrimaryButton>
                        </Box>
                        {soloAttendanceChecked ? (
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            ✓ No additional representatives will join — you will
                            attend the meeting yourself.
                          </Typography>
                        ) : emailIds.list.length > 0 ? (
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            ✓ {emailIds.list.length} attendee
                            {emailIds.list.length > 1 ? "s" : ""} added:{" "}
                            {emailIds.list.map((email, index) => (
                              <span key={email}>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    color: "primary.main",
                                    textDecoration: "underline",
                                    textDecorationColor: "primary.main",
                                  }}
                                >
                                  {email}
                                </Typography>
                                {index < emailIds.list.length - 1 && ", "}
                              </span>
                            ))}
                          </Typography>
                        ) : null}
                      </Box>
                    </Grid>
                  )}
              </Grid>
            )}

            {manualSelectionEnabled && currentStep === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <AppAlert severity="info" sx={{ fontSize: "small" }}>
                    Please enter the times according to your browser's time
                    zone; they will be automatically converted to the
                    ambassador's time zone.
                  </AppAlert>
                </Grid>
                {attendeesConfirmed && validateAttendeeRequirement() && (
                  <Grid item xs={12}>
                    <Box
                      sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Attendees
                        </Typography>
                        <PrimaryButton
                          onClick={() => setShowAddMembersModal(true)}
                        >
                          Change Attendees
                        </PrimaryButton>
                      </Box>
                      {soloAttendanceChecked ? (
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          ✓ No additional representatives will join — you will
                          attend the meeting yourself.
                        </Typography>
                      ) : emailIds.list.length > 0 ? (
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          ✓ {emailIds.list.length} attendee
                          {emailIds.list.length > 1 ? "s" : ""} added:{" "}
                          {emailIds.list.map((email, index) => (
                            <span key={email}>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  color: "primary.main",
                                  textDecoration: "underline",
                                  textDecorationColor: "primary.main",
                                }}
                              >
                                {email}
                              </Typography>
                              {index < emailIds.list.length - 1 && ", "}
                            </span>
                          ))}
                        </Typography>
                      ) : null}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <ManualSlotsSection
                    slots={slots}
                    browserTimezone={browserTimezone}
                    ambassadorTimezoneAbbr={ambassadorTimezoneAbbr}
                    ambassadorTimezoneValue={ambassadorTimezoneValue}
                    onAddSlot={handleOnSlotAdd}
                    onEditSlot={handleOnSlotEdit}
                    onRemoveSlot={handleOnSlotRemove}
                    showErrors={submitClicked}
                    formErrorMessage={
                      formErrorMessage &&
                        formErrorMessage !== "Invalid email addresses" &&
                        formErrorMessage !==
                        "Please either add attendee emails or confirm you will attend alone" &&
                        formErrorMessage !==
                        "Please add attendee email addresses or confirm that no additional representatives will join"
                        ? formErrorMessage
                        : undefined
                    }
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>

        <FlexBox
          justifyContent="space-between"
          position="sticky"
          bottom={0}
          zIndex={10}
          bgcolor="background.paper"
          borderTop={1}
          borderColor="divider"
          px={2}
          pt={2}
        >
          {shouldShowHelpButton ? (
            <RequestCoordinationButton
              invitationId={invitationId}
              initiatedBy="vendor"
              size="small"
              variant="outlined"
              color="secondary"
              onSuccess={onRequestCoordinationSuccess}
            />
          ) : (
            <Box />
          )}

          {manualSelectionEnabled ? (
            currentStep === 0 ? (
              <FlexBox gap={1.5} flexWrap="wrap" alignItems="center">
                <FormHelperTextError
                  error={formErrorMessage}
                  showIcon={!!formErrorMessage}
                />
                <FlexBox gap={1.5}>
                  <DefaultButton onClick={onCancel} sx={{ width: { sm: 180 } }}>
                    Cancel
                  </DefaultButton>
                  <PrimaryButton onClick={handleNextStep} sx={{ width: { sm: 180 } }}>
                    Continue
                  </PrimaryButton>
                </FlexBox>
              </FlexBox>
            ) : (
              <FlexBox gap={1.5} flexWrap="wrap" alignItems="center">
                <DefaultButton onClick={handleBackStep} sx={{ width: { sm: 180 } }}>Discard Changes</DefaultButton>
                <PrimaryButton
                  onClick={onSubmit}
                  disabled={submitting || invitationAmbassadorList?.slots?.length < 3}
                  loading={submitting}
                  sx={{ width: { sm: 180 } }}
                >
                  {!attendeesConfirmed ? "Continue" : "Next"}
                </PrimaryButton>
              </FlexBox>
            )
          ) : (
            <FlexBox gap={1.5} flexWrap="wrap" alignItems="center">
              <FormHelperTextError
                showIcon
                error={
                  formErrorMessage ===
                    "Please add attendee email addresses or confirm that no additional representatives will join"
                    ? undefined
                    : formErrorMessage
                }
              />
              <FlexBox gap={1.5}>
                <DefaultButton onClick={onCancel} sx={{ width: { sm: 180 } }}>
                  Cancel
                </DefaultButton>
                <PrimaryButton
                  onClick={onSubmit}
                  loading={submitting}
                  sx={{ width: { sm: !attendeesConfirmed ? 180 : "auto" } }}
                >
                  {!attendeesConfirmed ? "Continue" : "Send Calendly Invite"}
                </PrimaryButton>
              </FlexBox>
            </FlexBox>
          )}
        </FlexBox>
      </Stack>

      {showSlotSelectionConfirmAlert && (
        <AlertDialog
          message={
            GLOBAL_TEXT.ALERT_MESSAGES
              .CONFIRM_LATE_NIGHT_SLOT_SELECTION_AMBASSADOR
          }
          onPrimaryActionClick={handleLateNightConfirm}
          handleClose={handleLateNightDismiss}
        />
      )}

      {openAmbassadorProfileModal && (
        <ProfileViewModal
          onClose={() => setOpenAmbassadorProfileModal(false)}
          customComponent={
            <Stack
              sx={{
                maxHeight: "80vh",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", mb: 2 }}>
                <AmbassadorProfile
                  fetchData={false}
                  editable={false}
                  showCompanyName
                  data={data?.ambassador}
                  showIntrestedIn={false}
                />
              </Box>
              <Box
                component="footer"
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  justifyContent: "flex-end",
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <DefaultButton
                  onClick={() => setOpenAmbassadorProfileModal(false)}
                  sx={{ width: { sm: 180 } }}
                >
                  Close
                </DefaultButton>
                <PrimaryButton
                  onClick={onConfirm}
                  loading={submitting}
                  sx={{ width: { sm: 180 } }}
                >
                  Send Invite
                </PrimaryButton>
              </Box>
            </Stack>
          }
        />
      )}

      {slotModalState && (
        <SlotSelectionModal
          onClose={handleSlotModalClose}
          onSubmit={handleSlotSubmit}
          initialSelectedTimes={slots.map((s) => s.selectedSlotTimeFrom).filter(Boolean)}
          slotIndex={
            slotModalState.mode === "edit" && slotModalState.index !== null
              ? slotModalState.index + 1
              : slots.length + 1
          }
          slotDetails={
            slotModalState.mode === "edit" && slotModalState.index !== null
              ? slots[slotModalState.index]
              : null
          }
          oppositeUserTimezone={ambassadorTimezoneValue}
          userRole="ambassador"
          allowMultiSelect={
            slotModalState.mode === "create" && slotModalState.index === null
          }
          totalSlots={slots.length}
        />
      )}

      <AddMembersModal
        open={showAddMembersModal}
        onClose={() => {
          setShowAddMembersModal(false);
          setPendingSubmission(null);
        }}
        onSubmit={handleAddMembersSubmit}
        initialEmails={
          emailIds.list.length > 0
            ? emailIds.list
            : data?.additionalMembers
              ?.map((member: any) => member?.email)
              .filter(Boolean) || []
        }
        initialSoloAttendance={false}
        title="Add Attendees"
        submitButtonText="Add"
        cancelButtonText="Cancel"
      />
    </>
  );
}

export default VendorSlotSelectionModal;
